import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { money } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Modal, Field } from "../../components/common";

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => API.get("/admin/plans").then((r) => setPlans(r.data.plans));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await API.patch(`/admin/plans/${edit.id}`, {
        name: edit.name, tagline: edit.tagline, trial_days: Number(edit.trial_days),
        price_monthly: edit.price_monthly_cents != null ? edit.price_monthly_cents / 100 : "",
        price_yearly: edit.price_yearly_cents != null ? edit.price_yearly_cents / 100 : "",
        features: edit.features, active: edit.active,
        promo_code: edit.promo_code || "", discount_percent: edit.discount_percent || 0,
      });
      toast.success("Plan updated — pricing pages reflect this immediately");
      setEdit(null); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  if (!plans) return <Loading label="Loading plans" />;

  return (
    <div data-testid="admin-plans">
      <PageHeader title="Plans & Pricing" sub="Change prices, features, trials, promos, and billing cycles without code." />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => (
          <div key={p.id} className="card-gov p-5" data-testid={`admin-plan-${p.slug}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-navy">{p.name}</span>
              <StatusBadge status={p.active ? "ACTIVE" : "CANCELLED"} />
            </div>
            <div className="money mt-2 text-2xl font-bold text-slate-800">
              {p.custom ? "Custom" : `${money(p.price_monthly_cents)}/mo`}
            </div>
            {!p.custom && <div className="text-xs text-slate-400">{money(p.price_yearly_cents)}/yr · {p.trial_days}-day trial</div>}
            <ul className="mt-3 space-y-1">
              {(p.features || []).slice(0, 5).map((f) => <li key={f} className="text-xs text-slate-500">· {f}</li>)}
              {(p.features || []).length > 5 && <li className="text-xs text-slate-400">+{p.features.length - 5} more</li>}
            </ul>
            <button className="btn-outline-gov mt-4 w-full" onClick={() => setEdit({ ...p, features: (p.features || []).join("\n") })} data-testid={`edit-plan-${p.slug}`}>Edit Plan</button>
          </div>
        ))}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit Plan — ${edit?.name || ""}`}>
        {edit && (
          <form onSubmit={save} className="space-y-4" data-testid="plan-edit-form">
            <Field label="Plan Name"><input className="input-gov" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="plan-name" /></Field>
            <Field label="Tagline"><input className="input-gov" value={edit.tagline || ""} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} data-testid="plan-tagline" /></Field>
            {!edit.custom && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly Price ($)"><input className="input-gov money" inputMode="decimal" value={edit.price_monthly_cents != null ? edit.price_monthly_cents / 100 : ""} onChange={(e) => setEdit({ ...edit, price_monthly_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="plan-price-monthly" /></Field>
                <Field label="Annual Price ($)"><input className="input-gov money" inputMode="decimal" value={edit.price_yearly_cents != null ? edit.price_yearly_cents / 100 : ""} onChange={(e) => setEdit({ ...edit, price_yearly_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="plan-price-yearly" /></Field>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <Field label="Trial Days"><input type="number" className="input-gov" value={edit.trial_days} onChange={(e) => setEdit({ ...edit, trial_days: e.target.value })} data-testid="plan-trial" /></Field>
              <Field label="Promo Code"><input className="input-gov" value={edit.promo_code || ""} onChange={(e) => setEdit({ ...edit, promo_code: e.target.value })} data-testid="plan-promo" /></Field>
              <Field label="Discount %"><input type="number" className="input-gov" value={edit.discount_percent || 0} onChange={(e) => setEdit({ ...edit, discount_percent: e.target.value })} data-testid="plan-discount" /></Field>
            </div>
            <Field label="Features (one per line)"><textarea className="input-gov" rows={6} value={edit.features} onChange={(e) => setEdit({ ...edit, features: e.target.value })} data-testid="plan-features" /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} data-testid="plan-active" /> Plan is active and visible on pricing page
            </label>
            <button className="btn-navy w-full" data-testid="plan-save">Save Plan</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
